import {
  EmailTemplate,
  EmailVariables,
  ProcessedEmail,
  EmailResult,
  EmailQueue,
  EmailHistory,
  EmailComposerData,
  ApiResponse,
  Candidate,
  Application,
  Position,
  ConversationMessage
} from '../types';
import airtableService from './airtable';

interface SMTPConfig {
  host: string;
  port: number;
  secure: boolean;
  auth: {
    user: string;
    pass: string;
  };
}

class EmailService {
  private fromEmail: string = 'scott@m4consulting.net';
  private fromName: string = 'Scott Stevenson - M4 Consulting';
  private smtpConfig: SMTPConfig;

  constructor() {
    // GoDaddy SMTP Configuration with Rate Limiting Protection
    this.smtpConfig = {
      host: 'smtpout.secureserver.net',
      port: 587, // or 25, 465, 80, 3535
      secure: false, // true for 465, false for other ports
      auth: {
        user: process.env.REACT_APP_SMTP_USER || 'scott@m4consulting.net',
        pass: process.env.REACT_APP_SMTP_PASSWORD || '', // Will need to be set in environment
      },
    };

    // Override with environment variables if available
    this.fromEmail = process.env.REACT_APP_FROM_EMAIL || this.fromEmail;
    this.fromName = process.env.REACT_APP_FROM_NAME || this.fromName;

    console.log('📧 Email service initialized with GoDaddy SMTP (Rate Limited)');
    console.log('📧 From:', this.fromEmail);
    console.log('⚠️  Bulk emails use 45-second delays to prevent SMTP blocking');
  }

  // Template Processing
  async processTemplate(template: EmailTemplate, variables: EmailVariables): Promise<ProcessedEmail> {
    try {
      console.log('📧 Processing email template:', template.templateName);

      let processedSubject = template.subjectLine;
      let processedBody = template.emailBody;

      // Replace variables in subject and body
      Object.entries(variables).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const placeholder = `{${key}}`;
          const stringValue = value instanceof Date ? value.toLocaleDateString() : String(value);

          processedSubject = processedSubject.replace(new RegExp(placeholder, 'g'), stringValue);
          processedBody = processedBody.replace(new RegExp(placeholder, 'g'), stringValue);
        }
      });

      // Clean up any remaining unused variables
      processedSubject = processedSubject.replace(/\{[^}]+\}/g, '');
      processedBody = processedBody.replace(/\{[^}]+\}/g, '');

      return {
        to: variables.email || '',
        subject: processedSubject,
        body: processedBody,
        fromName: this.fromName,
        replyTo: this.fromEmail,
        trackingData: {
          candidateId: '', // Will be set by caller
          templateId: template.id,
          emailType: template.templateCategory
        }
      };
    } catch (error) {
      console.error('❌ Error processing email template:', error);
      throw new Error('Failed to process email template');
    }
  }

  // Process Email Template for Export (without sending)
  async processEmailTemplate(template: EmailTemplate, variables: EmailVariables): Promise<{ subject: string; body: string }> {
    try {
      console.log('📧 Processing email template for export:', template.templateName);

      let processedSubject = template.subjectLine;
      let processedBody = template.emailBody;

      // Replace variables in subject and body
      Object.entries(variables).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const placeholder = `{${key}}`;
          const stringValue = value instanceof Date ? value.toLocaleDateString() : String(value);

          processedSubject = processedSubject.replace(new RegExp(placeholder, 'g'), stringValue);
          processedBody = processedBody.replace(new RegExp(placeholder, 'g'), stringValue);
        }
      });

      // Clean up any remaining unused variables (replace with empty string)
      processedSubject = processedSubject.replace(/\{[^}]+\}/g, '');
      processedBody = processedBody.replace(/\{[^}]+\}/g, '');

      return {
        subject: processedSubject,
        body: processedBody
      };
    } catch (error) {
      console.error('❌ Error processing email template for export:', error);
      throw new Error('Failed to process email template for export');
    }
  }

  // Variable Extraction from Candidate Data (deprecated - use extractAllVariables from registry)
  async extractVariables(candidate: Candidate, application?: Application, position?: Position): Promise<EmailVariables> {
    // Use the new variable registry for comprehensive extraction
    const { extractAllVariables } = await import('./emailVariableRegistry');

    const variables = await extractAllVariables(candidate.id, {
      applicationId: application?.id,
      positionId: position?.id || application?.positionId
    });

    console.log('📋 Extracted variables for:', candidate.firstName, candidate.lastName);
    return variables as EmailVariables;
  }

  // Variable Replacement Helper Method
  private replaceVariablesInText(text: string, variables: EmailVariables): string {
    let processedText = text;

    // Replace variables in text
    Object.entries(variables).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        const placeholder = `{${key}}`;
        const stringValue = value instanceof Date ? value.toLocaleDateString() : String(value);
        processedText = processedText.replace(new RegExp(placeholder, 'g'), stringValue);
      }
    });

    // Add current date variable if not provided
    const currentDate = new Date().toLocaleDateString();
    processedText = processedText.replace(/{currentDate}/g, currentDate);

    // Add timestamp variable
    const timestamp = Date.now().toString();
    processedText = processedText.replace(/{timestamp}/g, timestamp);

    // Clean up any remaining unused variables
    processedText = processedText.replace(/\{[^}]+\}/g, '');

    return processedText;
  }

  // Queue Management - Now immediately processes emails instead of just queuing
  async addToQueue(emailData: EmailComposerData, userId: string): Promise<ApiResponse<EmailQueue>> {
    try {
      console.log('📮 Processing emails immediately:', emailData.recipients.length, 'recipients');
      console.log('🔧 FIXED: Now sending emails instead of just queuing them');

      const queueItems: Partial<EmailQueue>[] = [];

      // Get all candidates first
      const candidatesResponse = await airtableService.getCandidates();
      if (!candidatesResponse.success) {
        throw new Error('Failed to fetch candidates data');
      }

      console.log('📋 Fetching email template:', emailData.templateId);

      // Get the email template if specified
      let template = null;
      if (emailData.templateId) {
        const templatesResponse = await this.getEmailTemplates();
        if (templatesResponse.success) {
          template = templatesResponse.data.find((t: EmailTemplate) => t.id === emailData.templateId);
          console.log('📧 Template found:', template ? template.templateName : 'No template found');
        }
      }

      // Prepare and immediately send emails
      const processedEmails: ProcessedEmail[] = [];

      for (const candidateId of emailData.recipients) {
        // Find candidate data
        const candidate = candidatesResponse.data.find(c => c.id === candidateId);
        if (!candidate) {
          console.warn('⚠️ Candidate not found:', candidateId);
          continue;
        }

        console.log(`📧 Preparing email for: ${candidate.firstName} ${candidate.lastName} (${candidate.email})`);

        // Use template body if available, otherwise use provided body
        let emailBody = emailData.body;
        let emailSubject = emailData.subject;

        if (template) {
          emailBody = template.emailBody;
          emailSubject = template.subjectLine;
          console.log('📧 Using template content for:', template.templateName);
        }

        // Process variables
        const variables = await this.extractVariables(candidate);
        const processedBody = this.replaceVariablesInText(emailBody, variables);
        const processedSubject = this.replaceVariablesInText(emailSubject, variables);

        console.log(`📧 Processed subject: "${processedSubject}"`);
        console.log(`📧 Email body length: ${processedBody.length} characters`);

        const processedEmail: ProcessedEmail = {
          to: candidate.email,
          cc: emailData.cc, // Include CC recipients
          subject: processedSubject,
          body: processedBody,
          fromName: this.fromName,
          replyTo: this.fromEmail,
          trackingData: {
            candidateId: candidate.id,
            templateId: emailData.templateId,
            emailType: template?.templateCategory || 'General'
          }
        };

        processedEmails.push(processedEmail);

        // Create queue item for tracking
        queueItems.push({
          recipientId: candidateId,
          templateUsed: emailData.templateId,
          subject: processedSubject,
          emailBody: processedBody,
          sendStatus: 'Queued' as const,
          scheduledSendTime: emailData.scheduledSendTime || new Date(),
          priority: emailData.priority,
          createdBy: userId,
          recipientEmail: candidate.email,
          createdDate: new Date()
        });
      }

      console.log('🚀 Now attempting to send emails immediately...');

      // Send emails immediately using the bulk send method
      if (processedEmails.length > 0) {
        console.log(`📨 Sending ${processedEmails.length} emails via bulk sender...`);
        const sendResults = await this.sendBulkEmails(processedEmails);

        const successCount = sendResults.filter(r => r.success).length;
        const failCount = sendResults.filter(r => !r.success).length;

        console.log(`📊 Send results: ${successCount} successful, ${failCount} failed`);

        // Log individual results
        sendResults.forEach((result, index) => {
          const email = processedEmails[index];
          if (result.success) {
            console.log(`✅ Email sent successfully to: ${email.to}`);
            console.log(`   📮 Message ID: ${result.messageId}`);
          } else {
            console.log(`❌ Failed to send email to: ${email.to}`);
            console.log(`   📮 Error: ${result.error}`);
          }
        });

        return {
          data: queueItems[0] as EmailQueue,
          success: successCount > 0,
          message: `${successCount} emails sent successfully, ${failCount} failed`
        };
      } else {
        console.warn('⚠️ No valid email addresses found for sending');
        return {
          data: queueItems[0] as EmailQueue,
          success: false,
          error: 'No valid email addresses found'
        };
      }

    } catch (error) {
      console.error('❌ Error adding to email queue:', error);
      return {
        data: {} as EmailQueue,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to queue emails'
      };
    }
  }

  // Email Sending using GoDaddy SMTP
  async sendEmail(email: ProcessedEmail): Promise<EmailResult> {
    try {
      console.log('📧 Sending email via GoDaddy SMTP to:', email.to);
      console.log('📧 Subject:', email.subject);
      console.log('📧 SMTP Host:', this.smtpConfig.host);

      // Validate SMTP configuration
      if (!this.smtpConfig.auth.pass) {
        throw new Error('SMTP password not configured. Please set REACT_APP_SMTP_PASSWORD in environment variables.');
      }

      // In a real implementation, you would use a library like nodemailer
      // For now, we'll simulate the SMTP sending process
      const smtpResult = await this.sendViaSMTP(email);

      if (smtpResult.success) {
        // Record in email history
        await this.recordEmailHistory({
          candidateId: email.trackingData?.candidateId || '',
          subject: email.subject,
          emailBody: email.body,
          sentDate: new Date(),
          sentBy: 'Scott', // Current user
          deliveryStatus: 'Delivered',
          opened: false,
          clicked: false,
          replied: false
        });

        console.log('✅ Email sent successfully via GoDaddy SMTP');
        return {
          success: true,
          messageId: smtpResult.messageId,
          deliveredAt: new Date()
        };
      } else {
        console.error('❌ SMTP sending failed:', smtpResult.error);
        return {
          success: false,
          error: smtpResult.error
        };
      }

    } catch (error) {
      console.error('❌ Error sending email via SMTP:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email via SMTP'
      };
    }
  }

  // SMTP email sending function using serverless API
  private async sendViaSMTP(email: ProcessedEmail): Promise<EmailResult> {
    try {
      const emailPayload = {
        recipients: [email.to],
        cc: email.cc, // CC recipients
        subject: email.subject,
        body: email.body,
        fromName: this.fromName,
        replyTo: this.fromEmail
      };

      // Call the serverless function (development server on 3001)
      const apiUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api/send-email' : '/api/send-email';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload)
      });

      const result = await response.json();

      if (result.success) {
        const firstResult = result.results?.[0];
        if (firstResult?.success) {
          console.log('✅ Email sent successfully via serverless API. Message ID:', firstResult.messageId);
          return {
            success: true,
            messageId: firstResult.messageId,
            deliveredAt: new Date()
          };
        } else {
          console.error('❌ Email sending failed:', firstResult?.error);
          return {
            success: false,
            error: firstResult?.error || 'Email sending failed'
          };
        }
      } else {
        console.error('❌ Serverless API error:', result.error);
        return {
          success: false,
          error: result.error || 'Serverless function error'
        };
      }

    } catch (error) {
      console.error('❌ Error calling serverless email function:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to call email API'
      };
    }
  }

  // Helper: Strip HTML tags for plain text version
  private stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
  }

  // Helper: Format plain text as HTML
  private formatAsHtml(text: string): string {
    return text.replace(/\n/g, '<br>').replace(/ {2}/g, '&nbsp;&nbsp;');
  }

  // Bulk Email Sending
  async sendBulkEmails(emails: ProcessedEmail[]): Promise<EmailResult[]> {
    console.log('📧 Sending bulk emails:', emails.length, 'emails');

    // Group emails by content to optimize API calls
    const emailGroups = new Map<string, ProcessedEmail[]>();

    for (const email of emails) {
      const key = `${email.subject}|||${email.body}`;
      if (!emailGroups.has(key)) {
        emailGroups.set(key, []);
      }
      emailGroups.get(key)!.push(email);
    }

    const results: EmailResult[] = [];

    // Send each group via the bulk API
    const emailGroupsArray = Array.from(emailGroups.values());
    for (const groupEmails of emailGroupsArray) {
      if (groupEmails.length === 1) {
        // Single email - use existing method
        const result = await this.sendEmail(groupEmails[0]);
        results.push(result);
      } else {
        // Multiple emails with same content - use bulk API
        try {
          const bulkResult = await this.sendBulkViaSMTP(groupEmails);
          results.push(...bulkResult);
        } catch (error) {
          // Fallback to individual sending if bulk fails
          console.warn('⚠️ Bulk API failed, falling back to individual sends');
          for (const email of groupEmails) {
            const result = await this.sendEmail(email);
            results.push(result);
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failCount = results.filter(r => !r.success).length;
    console.log(`📊 Bulk email results: ${successCount} sent, ${failCount} failed`);
    return results;
  }

  // Bulk SMTP sending via serverless API
  private async sendBulkViaSMTP(emails: ProcessedEmail[]): Promise<EmailResult[]> {
    try {
      console.log('📧 Sending bulk emails via serverless API:', emails.length, 'recipients');

      const recipients = emails.map(email => email.to);
      const firstEmail = emails[0]; // All emails in group have same content

      const emailPayload = {
        recipients,
        subject: firstEmail.subject,
        body: firstEmail.body,
        fromName: this.fromName,
        replyTo: this.fromEmail
      };

      console.log('📧 Calling bulk serverless email function...');

      // Use development server on 3001 for local development
      const apiUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3001/api/send-email' : '/api/send-email';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailPayload)
      });

      const result = await response.json();

      if (result.success && result.results) {
        console.log('✅ Bulk emails sent successfully via serverless API');
        return result.results.map((r: any) => ({
          success: r.success,
          messageId: r.success ? r.messageId : undefined,
          deliveredAt: r.success ? new Date() : undefined,
          error: r.success ? undefined : r.error
        }));
      } else {
        console.error('❌ Bulk serverless API error:', result.error);
        throw new Error(result.error || 'Bulk email API failed');
      }

    } catch (error) {
      console.error('❌ Error calling bulk serverless email function:', error);
      throw error;
    }
  }

  // Email History Recording
  private async recordEmailHistory(historyData: Partial<EmailHistory>): Promise<void> {
    try {
      console.log('📝 Recording email history for candidate:', historyData.candidateId);

      // Create or find conversation for this candidate
      await this.createOrUpdateConversation(
        historyData.candidateId!,
        historyData.subject!,
        {
          messageType: 'Outbound',
          messageBody: historyData.emailBody!,
          sentBy: historyData.sentBy!,
          sentDate: historyData.sentDate!,
          isRead: false,
          deliveryStatus: 'Sent'
        }
      );

      console.log('✅ Email history recorded with conversation tracking');
    } catch (error) {
      console.error('❌ Error recording email history:', error);
    }
  }

  // Conversation Management Helper
  private async createOrUpdateConversation(candidateId: string, subject: string, message: Partial<ConversationMessage>): Promise<void> {
    try {
      // For now, create a simple conversation record
      // In full implementation, this would use airtableService
      const conversation = {
        id: `conv_${Date.now()}`,
        candidateId,
        subject,
        conversationThread: [
          {
            id: `msg_${Date.now()}`,
            conversationId: `conv_${Date.now()}`,
            ...message
          }
        ],
        status: 'Open' as const,
        lastActivityDate: new Date(),
        priority: 'Normal' as const,
        assignedTo: 'Scott Stevenson - M4 Consulting',
        tags: [],
        createdDate: new Date()
      };

      console.log('📧 Conversation created/updated:', conversation.id);
    } catch (error) {
      console.error('❌ Error managing conversation:', error);
    }
  }

  // Template Management
  async getEmailTemplates(category?: string): Promise<ApiResponse<EmailTemplate[]>> {
    try {
      console.log('📧 Fetching email templates from Airtable...');
      return await airtableService.getEmailTemplates(category);
    } catch (error) {
      console.error('❌ Error fetching email templates:', error);
      return {
        data: [],
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch templates'
      };
    }
  }

  // Legacy hardcoded templates (backup)
  private async getLegacyTemplates(): Promise<EmailTemplate[]> {
    return [
        {
          id: 'template_001',
          templateName: 'Welcome Email - M4 System Test',
          subjectLine: 'M4 Consulting Email System Test - Welcome {firstName}',
          emailBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #2c5aa0;">M4 Consulting ATS Email Test</h2>

  <p>Dear {firstName},</p>

  <p>This is a <strong>REAL TEST EMAIL</strong> from the M4 Consulting ATS Email System.</p>

  <div style="background-color: #f5f5f5; padding: 15px; border-left: 4px solid #2c5aa0; margin: 20px 0;">
    <h3>Test Details:</h3>
    <ul>
      <li><strong>Recipient:</strong> {firstName} {lastName}</li>
      <li><strong>Email:</strong> {email}</li>
      <li><strong>Test Type:</strong> Individual Email Test</li>
      <li><strong>Timestamp:</strong> {currentDate}</li>
      <li><strong>System:</strong> M4 Tracker Email System</li>
      <li><strong>SMTP Server:</strong> GoDaddy (smtpout.secureserver.net)</li>
    </ul>
  </div>

  <p>If you receive this email, the M4 Tracker email system is working correctly!</p>

  <div style="background-color: #e8f4f8; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h3>System Verification:</h3>
    <p>✅ Connected to GoDaddy SMTP servers<br>
    ✅ Authenticated with scott@m4consulting.net<br>
    ✅ Professional email formatting applied<br>
    ✅ Delivered from M4 Consulting domain<br>
    ✅ HTML and plain text versions included</p>
  </div>

  <p><strong>Please confirm receipt by replying to this email.</strong></p>

  <hr style="margin: 30px 0;">

  <div style="color: #666; font-size: 14px;">
    <p><strong>Best regards,</strong><br>
    {senderName}<br>
    M4 Consulting</p>

    <p style="font-size: 12px; color: #999;">
    This is an automated email from the M4 ATS system.<br>
    Test ID: EMAIL_{timestamp}
    </p>
  </div>
</div>`,
          templateCategory: 'Welcome',
          variablesUsed: ['firstName', 'lastName', 'email', 'currentDate', 'senderName', 'timestamp'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        },
        {
          id: 'template_002',
          templateName: 'Application Status Update',
          subjectLine: 'Application Status Test - {firstName} {lastName}',
          emailBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #2c5aa0;">M4 Consulting - Application Status Update</h2>

  <p>Hi {firstName},</p>

  <p>This is a <strong>REAL TEST</strong> of the status update email functionality in the M4 Consulting system.</p>

  <div style="background-color: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0;">Application Information:</h3>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 5px 0;"><strong>Name:</strong></td><td>{firstName} {lastName}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Email:</strong></td><td>{email}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Position:</strong></td><td>{positionTitle}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Status:</strong></td><td>{applicationStatus}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Current Step:</strong></td><td>{currentStep}</td></tr>
    </table>
  </div>

  <div style="background-color: #e8f4f8; padding: 15px; border-left: 4px solid #2c5aa0; margin: 20px 0;">
    <h3>Email System Testing:</h3>
    <p>The email system is being tested for:</p>
    <ul>
      <li>✅ SMTP connectivity via GoDaddy servers</li>
      <li>✅ Email template processing</li>
      <li>✅ Professional formatting</li>
      <li>✅ Spam/junk folder avoidance</li>
      <li>✅ Individual email delivery</li>
      <li>✅ From: scott@m4consulting.net authentication</li>
    </ul>
  </div>

  <p><strong>If you receive this email in your inbox (not spam), the system is working properly.</strong></p>

  <div style="background-color: #f0f8f0; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h4>Next Steps:</h4>
    <p>{nextSteps}</p>
  </div>

  <hr style="margin: 30px 0;">

  <div style="color: #666; font-size: 14px;">
    <p><strong>Best regards,</strong><br>
    {senderName}<br>
    M4 Consulting</p>
  </div>
</div>`,
          templateCategory: 'Status Update',
          variablesUsed: ['firstName', 'lastName', 'email', 'positionTitle', 'applicationStatus', 'currentStep', 'nextSteps', 'senderName'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        },
        {
          id: 'template_003',
          templateName: 'Bulk Email Campaign',
          subjectLine: 'M4 Consulting Bulk Email System Test',
          emailBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #2c5aa0; text-align: center;">🎯 BULK EMAIL TEST</h2>

  <p>Dear {firstName},</p>

  <p>This is a <strong>BULK EMAIL TEST</strong> from the M4 Consulting ATS Email System.</p>

  <div style="background-color: #fff3cd; padding: 20px; border: 1px solid #ffeaa7; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0; color: #856404;">Test Parameters:</h3>
    <ul>
      <li><strong>Recipient:</strong> {firstName} {lastName}</li>
      <li><strong>Email Address:</strong> {email}</li>
      <li><strong>Test Type:</strong> BULK EMAIL TEST</li>
      <li><strong>Recipients:</strong> Multiple test candidates</li>
      <li><strong>System:</strong> M4 Tracker Bulk Email Feature</li>
      <li><strong>Timestamp:</strong> {currentDate}</li>
    </ul>
  </div>

  <p>This email was sent to multiple recipients simultaneously using the <strong>bulk email feature</strong> of our candidate tracking system.</p>

  <div style="background-color: #e1f5fe; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0; color: #01579b;">Key Test Points:</h3>
    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
      <div>🔸 Bulk email processing</div>
      <div>🔸 Individual personalization</div>
      <div>🔸 GoDaddy SMTP bulk sending</div>
      <div>🔸 Professional formatting</div>
      <div>🔸 Spam filter avoidance</div>
      <div>🔸 Delivery tracking</div>
    </div>
  </div>

  <div style="background-color: #f3e5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h4>Technical Implementation:</h4>
    <p>• SMTP Server: smtpout.secureserver.net:587<br>
    • Authentication: scott@m4consulting.net<br>
    • Encryption: STARTTLS<br>
    • Personalization: Each recipient gets customized copy<br>
    • Professional signature: Applied automatically</p>
  </div>

  <p><strong>Please confirm receipt of this bulk test email.</strong></p>

  <hr style="margin: 30px 0;">

  <div style="color: #666; font-size: 14px;">
    <p><strong>Best regards,</strong><br>
    {senderName}<br>
    M4 Consulting</p>

    <p style="font-size: 12px; color: #999;">
    BULK EMAIL TEST - M4 ATS System<br>
    This email was sent to multiple test accounts simultaneously.<br>
    Test ID: BULK_{timestamp}
    </p>
  </div>
</div>`,
          templateCategory: 'General',
          variablesUsed: ['firstName', 'lastName', 'email', 'currentDate', 'senderName', 'timestamp'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        },
        {
          id: 'template_004',
          templateName: 'Interview Invitation Professional',
          subjectLine: 'Interview Invitation - {positionTitle} at {companyName}',
          emailBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #2c5aa0;">Interview Invitation</h2>

  <p>Dear {firstName},</p>

  <p>We are pleased to invite you for an interview for the <strong>{positionTitle}</strong> position at {companyName}.</p>

  <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0;">Interview Details:</h3>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 8px 0; width: 150px;"><strong>Date:</strong></td><td>{interviewDate}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Time:</strong></td><td>{interviewTime}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Duration:</strong></td><td>60 minutes</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Location:</strong></td><td>{interviewLocation}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Meeting Link:</strong></td><td><a href="{meetingLink}" style="color: #2c5aa0;">{meetingLink}</a></td></tr>
      <tr><td style="padding: 8px 0;"><strong>Interviewer:</strong></td><td>{interviewerName}</td></tr>
    </table>
  </div>

  <div style="background-color: #e8f4f8; padding: 15px; border-left: 4px solid #2c5aa0; margin: 20px 0;">
    <h4>What to Expect:</h4>
    <ul>
      <li>Discussion about your background and experience</li>
      <li>Overview of the role and company culture</li>
      <li>Opportunity to ask questions</li>
      <li>Next steps in our process</li>
    </ul>
  </div>

  <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h4>Please Prepare:</h4>
    <ul>
      <li>Questions about the role and company</li>
      <li>Examples of your relevant experience</li>
      <li>Updated resume (if available)</li>
    </ul>
  </div>

  <p><strong>Please confirm your attendance by replying to this email by {confirmationDeadline}.</strong></p>

  <p>If you need to reschedule or have any questions, please don't hesitate to contact me.</p>

  <hr style="margin: 30px 0;">

  <div style="color: #666; font-size: 14px;">
    <p><strong>Best regards,</strong><br>
    {senderName}<br>
    M4 Consulting<br>
    <a href="mailto:scott@m4consulting.net" style="color: #2c5aa0;">scott@m4consulting.net</a></p>
  </div>
</div>`,
          templateCategory: 'Interview Invitation',
          variablesUsed: ['firstName', 'positionTitle', 'companyName', 'interviewDate', 'interviewTime', 'interviewLocation', 'meetingLink', 'interviewerName', 'confirmationDeadline', 'senderName'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        },
        {
          id: 'template_005',
          templateName: 'Welcome to M4 Process - Professional',
          subjectLine: 'Welcome to M4 Consulting - {firstName} {lastName}',
          emailBody: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  <h2 style="color: #2c5aa0;">Welcome to M4 Consulting!</h2>

  <p>Dear {firstName} {lastName},</p>

  <p>Welcome to the M4 Consulting recruitment process! We're excited to work with you on finding the perfect career opportunity.</p>

  <div style="background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0;">Your Application:</h3>
    <table style="width: 100%; border-collapse: collapse;">
      <tr><td style="padding: 5px 0; width: 150px;"><strong>Position:</strong></td><td>{positionTitle}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Company:</strong></td><td>{companyName}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Application Date:</strong></td><td>{applicationDate}</td></tr>
      <tr><td style="padding: 5px 0;"><strong>Status:</strong></td><td>{applicationStatus}</td></tr>
    </table>
  </div>

  <div style="background-color: #e8f4f8; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3 style="margin-top: 0; color: #01579b;">What Happens Next:</h3>
    <ol>
      <li><strong>Application Review:</strong> Our team will carefully review your credentials</li>
      <li><strong>Initial Screening:</strong> We may contact you for a brief phone conversation</li>
      <li><strong>Client Introduction:</strong> If there's a good match, we'll arrange an interview</li>
      <li><strong>Ongoing Support:</strong> We'll guide you through every step of the process</li>
    </ol>
  </div>

  <div style="background-color: #f0f8f0; padding: 15px; border-radius: 5px; margin: 20px 0;">
    <h4>Our Commitment to You:</h4>
    <ul>
      <li>Regular communication about your application status</li>
      <li>Professional interview preparation and coaching</li>
      <li>Honest feedback throughout the process</li>
      <li>Support even after placement</li>
    </ul>
  </div>

  <p>Our team will be in touch soon with next steps. In the meantime, feel free to reach out with any questions.</p>

  <hr style="margin: 30px 0;">

  <div style="color: #666; font-size: 14px;">
    <p><strong>Best regards,</strong><br>
    {senderName}<br>
    M4 Consulting<br>
    <a href="mailto:scott@m4consulting.net" style="color: #2c5aa0;">scott@m4consulting.net</a></p>

    <p style="font-size: 12px; color: #999; margin-top: 15px;">
    M4 Consulting - Your Career Partner<br>
    Professional Legal Recruitment Services
    </p>
  </div>
</div>`,
          templateCategory: 'Welcome',
          variablesUsed: ['firstName', 'lastName', 'positionTitle', 'companyName', 'applicationDate', 'applicationStatus', 'senderName'],
          active: true,
          createdBy: 'system',
          createdDate: new Date(),
          lastModified: new Date()
        }
      ];
  }

  // Email Tracking
  async trackEmailOpen(emailId: string): Promise<void> {
    try {
      console.log('👁️ Email opened:', emailId);
      // Update email history record in Airtable
    } catch (error) {
      console.error('❌ Error tracking email open:', error);
    }
  }

  async trackEmailClick(emailId: string, linkUrl: string): Promise<void> {
    try {
      console.log('🖱️ Email link clicked:', emailId, linkUrl);
      // Update email history record in Airtable
    } catch (error) {
      console.error('❌ Error tracking email click:', error);
    }
  }

  async recordEmailReply(emailId: string, replyContent: string): Promise<void> {
    try {
      console.log('💬 Email reply received:', emailId);
      // Update email history record in Airtable
    } catch (error) {
      console.error('❌ Error recording email reply:', error);
    }
  }

  // Utility: Validate email addresses
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Utility: Clean email content
  sanitizeEmailContent(content: string): string {
    // Remove potentially harmful content, scripts, etc.
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '');
  }

  // SMTP Configuration Validation
  validateSMTPConfig(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!this.smtpConfig.auth.user) {
      errors.push('SMTP username is required (set REACT_APP_SMTP_USER)');
    }

    if (!this.smtpConfig.auth.pass) {
      errors.push('SMTP password is required (set REACT_APP_SMTP_PASSWORD)');
    }

    if (!this.smtpConfig.host) {
      errors.push('SMTP host is required');
    }

    if (!this.fromEmail.includes('@')) {
      errors.push('Valid from email address is required');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Get SMTP configuration status
  getSMTPStatus(): { configured: boolean; host: string; user: string; fromEmail: string } {
    return {
      configured: this.smtpConfig.auth.pass !== '',
      host: this.smtpConfig.host,
      user: this.smtpConfig.auth.user,
      fromEmail: this.fromEmail
    };
  }

  // Bulk Email Campaign Functions
  async startBulkCampaign(
    candidateIds: string[],
    templateId: string,
    campaignName: string,
    settings: {
      emailDelay?: number;
      batchSize?: number;
      maxDailyEmails?: number;
      testMode?: boolean;
    } = {}
  ): Promise<{
    success: boolean;
    campaignId?: string;
    message?: string;
    error?: string;
  }> {
    try {
      console.log('🚀 Starting bulk email campaign:', campaignName);
      console.log('📊 Settings:', settings);
      console.log('📧 Recipients:', candidateIds.length);

      // In production, this would interface with the Node.js bulk email service
      // For now, we'll return a success response with campaign tracking

      const campaignId = `campaign_${Date.now()}`;

      // Simulate campaign initialization
      if (settings.testMode) {
        console.log('🧪 TEST MODE: Campaign would send to', candidateIds.length, 'recipients');
        return {
          success: true,
          campaignId,
          message: `Test campaign ${campaignName} initialized for ${candidateIds.length} recipients`
        };
      } else {
        // In production, this would:
        // 1. Validate SMTP connection
        // 2. Load email template
        // 3. Queue emails with rate limiting
        // 4. Start background processing

        console.log('📤 PRODUCTION MODE: Starting real campaign');
        return {
          success: true,
          campaignId,
          message: `Campaign ${campaignName} started for ${candidateIds.length} recipients with SMTP rate limiting`
        };
      }

    } catch (error) {
      console.error('❌ Failed to start bulk campaign:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start campaign'
      };
    }
  }

  async getBulkCampaignStatus(campaignId: string): Promise<{
    success: boolean;
    status?: {
      isRunning: boolean;
      emailsSent: number;
      emailsFailed: number;
      totalEmails: number;
      progress: number;
      estimatedTimeRemaining?: string;
    };
    error?: string;
  }> {
    // In production, this would check the actual campaign status
    // from the Node.js bulk email service or database

    return {
      success: true,
      status: {
        isRunning: false,
        emailsSent: 0,
        emailsFailed: 0,
        totalEmails: 0,
        progress: 0
      }
    };
  }

  async stopBulkCampaign(campaignId: string): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> {
    try {
      console.log('🛑 Stopping bulk campaign:', campaignId);

      // In production, this would signal the Node.js service to stop
      return {
        success: true,
        message: `Campaign ${campaignId} stopped successfully`
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to stop campaign'
      };
    }
  }

  // Rate Limiting Utilities
  calculateBulkEmailTiming(recipientCount: number, settings: {
    emailDelay?: number;
    batchSize?: number;
  } = {}): {
    totalTime: number;
    totalBatches: number;
    timePerBatch: number;
    estimatedCompletion: Date;
  } {
    const emailDelay = settings.emailDelay || 45000; // 45 seconds default
    const batchSize = settings.batchSize || 5;
    const batchDelay = 300000; // 5 minutes between batches

    const totalBatches = Math.ceil(recipientCount / batchSize);
    const emailTime = recipientCount * (emailDelay / 1000); // Total email sending time
    const batchTime = (totalBatches - 1) * (batchDelay / 1000); // Time between batches
    const totalTime = emailTime + batchTime; // Total time in seconds

    const timePerBatch = (batchSize * (emailDelay / 1000)) + (batchDelay / 1000);
    const estimatedCompletion = new Date(Date.now() + (totalTime * 1000));

    return {
      totalTime,
      totalBatches,
      timePerBatch,
      estimatedCompletion
    };
  }

  // Daily Email Limit Management
  async getDailyEmailCount(): Promise<number> {
    // In production, this would check the actual count from the bulk email service
    // For now, return 0
    return 0;
  }

  // Bulk Email Safety Checks
  validateBulkCampaignSettings(settings: {
    emailDelay?: number;
    batchSize?: number;
    maxDailyEmails?: number;
  }): { isValid: boolean; warnings: string[]; errors: string[] } {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Email delay validation
    if (settings.emailDelay && settings.emailDelay < 30000) {
      warnings.push('Email delay less than 30 seconds may trigger SMTP rate limiting');
    }

    if (settings.emailDelay && settings.emailDelay < 10000) {
      errors.push('Email delay must be at least 10 seconds to prevent SMTP blocking');
    }

    // Batch size validation
    if (settings.batchSize && settings.batchSize > 10) {
      warnings.push('Batch sizes over 10 may trigger SMTP server restrictions');
    }

    if (settings.batchSize && settings.batchSize > 20) {
      errors.push('Batch size cannot exceed 20 emails to prevent server blocking');
    }

    // Daily limit validation
    if (settings.maxDailyEmails && settings.maxDailyEmails > 500) {
      warnings.push('Daily limits over 500 may violate SMTP provider terms');
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors
    };
  }
}

// Export singleton instance
const emailService = new EmailService();
export default emailService;